import { ConfirmationDialogWithReasonComponent } from './../confirmation-dialog-with-reason/confirmation-dialog-with-reason.component';
import { MrvegasTaskProgressDialogComponent, MrVegasProgressDialogOutput } from './../../../../../../misc/mrvegas-task-progress-dialog/mrvegas-task-progress-dialog.component';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { awaitFor } from 'src/app/classes/misc';
import { PracticePathBaseInformations } from 'src/app/classes/model/practice-path';
import { FinalVideo, PracticeVideo, videoSorter } from 'src/app/classes/model/practice-video';
import { PracticePathService } from 'src/app/services/practice-path.service';
import { PracticeVideoService } from 'src/app/services/practice-video.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { NotifierService } from 'src/app/services/common/notifier-service.service';
import { BehaviorSubject } from 'rxjs';
import { ProgressDialogComponent } from 'src/app/modules/shared-module/components/progress-dialog/progress-dialog.component';
import { PermissionService } from 'src/app/services/common/permission.service';
import { Permission } from 'src/app/classes/model/permissions';
import { DirectBucketUploadError, DirectBucketUploadProgress, formatTransferredBytes } from 'src/app/services/common/direct-bucket-upload.service';
import { ConfirmationDialogService } from 'src/app/modules/confirmation-dialog/services/confirmation-dialog.service';

@Component({
  selector: 'app-final-videos-tab',
  templateUrl: './final-videos-tab.component.html',
  styleUrls: ['./final-videos-tab.component.scss']
})
export class FinalVideosTabComponent implements OnInit, OnChanges {
  @Input() finalVideos: Array<FinalVideo>;
  @Input() practicePath: PracticePathBaseInformations;


  protected startCloudInstance: boolean = true;

  @Output() clickedOnAnonymizedVideoEvent: EventEmitter<string>;
  @Output() finalVideoUploaded: EventEmitter<PracticeVideo>;

  @ViewChild("videoInput") videoInput: ElementRef<HTMLInputElement>;
  @ViewChild("finalVideosDataTable") finalVideosDataTable: ElementRef<HTMLTableElement>;

  protected isFinalVideoUploadInProgress: boolean = false;

  protected async onFinalVideoFileSelected(): Promise<void> {
    const input: HTMLInputElement = this.videoInput.nativeElement;
    const file: File | null = input.files?.item(0) ?? null;
    // Reset the input, so choosing the same file again (to resume the upload) triggers a change event too
    input.value = "";

    if (file == null || this.isFinalVideoUploadInProgress) {
      return;
    }

    if (!file.name.toLowerCase().endsWith(".mp4")) {
      this.notifierService.notify("error", "A videónak .mp4 kiterjesztésűnek kell lennie.");
      return;
    }

    this.isFinalVideoUploadInProgress = true;
    try {
      await this.uploadFinalVideo(file);
    } finally {
      this.isFinalVideoUploadInProgress = false;
    }
  }

  private async uploadFinalVideo(file: File): Promise<void> {
    const cancelController: AbortController = new AbortController();
    let cancelConfirmationDialog: MatDialogRef<unknown> | null = null;
    // The confirmation is pointless once the upload can not be cancelled any more
    const closeCancelConfirmation = (): void => {
      if (cancelConfirmationDialog?.getState() === MatDialogState.OPEN) {
        cancelConfirmationDialog.close();
      }
    };

    // Create the progress and the text subject for the upload
    const progressSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    const textSubject: BehaviorSubject<string> = new BehaviorSubject<string>("Feltöltés előkészítése...");
    const cancellableSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    const onUploadProgress = (state: "upload" | "save", progress: number, details?: DirectBucketUploadProgress) => {
      if (cancelController.signal.aborted) {
        return;
      }

      progressSubject.next(progress / 100);
      cancellableSubject.next(state === "upload");

      if (state === "upload") {
        const label: string = details?.isResumed ? "Feltöltés folytatása" : "Feltöltés";
        textSubject.next(details ? `${label}: ${formatTransferredBytes(details.uploadedBytes, details.totalBytes)}` : `${label}...`);
      } else {
        textSubject.next("Videó mentése a szerveren...");
        closeCancelConfirmation();
      }
    }

    // Open the progress dialog
    const uploadProgressDialogRef = ProgressDialogComponent.open(this.dialogService, {
      progressSubject: progressSubject,
      textSubject: textSubject,
      cancellableSubject: cancellableSubject,
      onCancel: () => {
        cancelConfirmationDialog = this.confirmationDialogService.open(
          "Feltöltés megszakítása",
          "Biztosan megszakítod a feltöltést? A már feltöltött részek törlődnek, a videót később elölről kell feltölteni.",
          () => {
            // The upload may have finished while the confirmation was open
            if (!cancellableSubject.value || cancelController.signal.aborted) {
              return;
            }

            cancellableSubject.next(false);
            textSubject.next("Feltöltés megszakítása...");
            cancelController.abort();
          },
          undefined,
          "Megszakítás",
          "Folytatás"
        );
      }
    });

    try {
      const practiceVideo: PracticeVideo | null = await this.practicePathService.uploadFinalPracticePathVideo(
        this.practicePath.uuid,
        { name: file.name, file: file },
        onUploadProgress,
        cancelController.signal
      );
      // Null if the saved video could not be fetched: the dialog refetches the path periodically, it appears then
      if (practiceVideo != null) {
        this.selectIndex(0);
        this.finalVideoUploaded.emit(practiceVideo);
        this.finalVideos = this.practicePath.video.finalVideos;
        this.changeDetectorRef.detectChanges();
        this.finalVideosDataTable?.nativeElement.scrollTo({ top: 0 });
      }
      this.notifierService.notify("success", "A végleges videó feltöltése sikeres.");
    } catch (error: any) {
      if (DirectBucketUploadError.isCancellation(error)) {
        this.notifierService.notify("info", "A feltöltés megszakítva.");
      } else {
        // The error message is already shown, choosing the same file again resumes the upload
        console.error(error);
      }
    }
    closeCancelConfirmation();
    uploadProgressDialogRef.close();
  }

  selectedIndex: number | null = null;

  // Annak az indexe amit éppen release-elünk
  releasingIndex: number | null = null;

  finalVideoDeletionsInProgress: Array<string> = [];

  constructor(
    private practiceVideoService: PracticeVideoService,
    private practicePathService: PracticePathService,
    private dialogService: MatDialog,
    private notifierService: NotifierService,
    private ngxUiService: NgxUiLoaderService,
    private permissionService: PermissionService,
    private changeDetectorRef: ChangeDetectorRef,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.clickedOnAnonymizedVideoEvent = new EventEmitter<string>();
    this.finalVideoUploaded = new EventEmitter<PracticeVideo>();
  }

  ngOnInit(): void {
    if (this.finalVideos.length > 0) {
      this.selectedIndex = 0;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If there are changes, resort the final videos
    this.finalVideos.sort(videoSorter);
  }

  public selectIndex(newIndex: number): void {
    this.selectedIndex = newIndex;
  }

  public selectFinalVideoToUpload(): void {
    this.videoInput.nativeElement.click();
  }

  public navigateToAnonymizedVideo(anonimizedVideoUuid: string): void {
    this.clickedOnAnonymizedVideoEvent.emit(anonimizedVideoUuid);
  }

  public async releaseFinalVideo(index: number): Promise<void> {
    if (this.releasingIndex !== null || index >= this.finalVideos.length) {
      return;
    }

    if (this.finalVideos[index].isPermanentDeleted) {
      this.notifierService.notify('error', 'Ezt a videót nem tudot kiadni, mivel törölve lett!');
      return;
    }

    this.releasingIndex = index;


    try {
      const taskId: string = await this.practiceVideoService.releaseFinalVideo(this.practicePath, this.finalVideos[index].uuid, this.startCloudInstance);
      if (taskId == null) {
        this.notifierService.notify("error", "Hiba történt a task létrehozásakor");
        return;
      }
      const mrVegasTaskProgressDialogRef = MrvegasTaskProgressDialogComponent.openDialog(this.dialogService, {
        taskId: taskId
      });

      // Ezen a ponton kilépett a dialógusból a felhasználó (csak akkor léphetett ki ha terminate state-be került a task)
      mrVegasTaskProgressDialogRef.afterClosed().subscribe(async (dialogOutput: MrVegasProgressDialogOutput) => {
        if (dialogOutput.lastTaskState.state == "DONE") {
          this.ngxUiService.start();
          await awaitFor(25000); // a szerver is lementi a videót... kb 25-30 másodpercenként checkolja, hogy végzett-e egy mrvegas task
          this.practicePath.video.releasedVideo = (
            await this.practicePathService.fetchPracticePath(this.practicePath.uuid, "summary")
          ).video.releasedVideo;

          this.ngxUiService.stop();
        }
      });
    } catch (error: any) {
      this.ngxUiService.stop();
      console.error(error);
    }

    this.releasingIndex = null;
  }

  protected async deleteFinalVideo(index: number): Promise<void> {



    const dialog = ConfirmationDialogWithReasonComponent.openDialog(this.dialogService, {
      title: "Végleges videó törlése",
      onYes: async (reason: string) => {
        dialog.close();

        this.finalVideoDeletionsInProgress.push(this.finalVideos[index].uuid);
        try {
          await this.practiceVideoService.deleteFinalVideo(this.practicePath, this.finalVideos[index].uuid, reason);
          this.notifierService.notify('success', 'A videó sikeresen törölve lett!');
        } catch (error: any) {
          console.error(error);
        }

        this.finalVideoDeletionsInProgress.removeItems(item => item === this.finalVideos[index].uuid);
      },
      onNo: () => {
        dialog.close();
      }
    });


  }

  protected isFinalVideoDeletionInProgress(index: number) {
    return this.finalVideoDeletionsInProgress.includes(this.finalVideos[index].uuid);
  }


  protected hasAdminFinalVideoUploadPermisison(): boolean {
    return this.permissionService.isLoggedUserHasPermission(Permission.PracticeVideoUpload);
  }

}
